import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Checkbox, Typography, FormControlLabel } from '@mui/material';
import TickIcon from 'components/Icons/TickIcon';
import CheckedCircleOutlineIcon from 'components/Icons/CheckedCircleOutlineIcon';
import CheckedCircleHeavyIcon from 'components/Icons/CheckedCircleHeavyIcon';
import { Player } from 'components/CamPlayerKbygRewinds/usePlayerState/usePlayerState';
import { trackEvent } from '@surfline/web-common';
import styles from './ShareForm.module.scss';

interface Props {
  formattedTime: string;
  rewindShareUrl: string;
  liveCamShareUrl: string;
  highlightsShareUrl: string;
  isRewindPlayer: boolean;
  onShareUrlChange: (url: string) => void;
  spotId: string;
  spotName: string;
  cameraId: string;
  cameraTitle: string;
  currentPlayer?: Player;
  clipIndex?: number;
}

const ShareForm: React.FC<Props> = ({
  formattedTime,
  rewindShareUrl,
  liveCamShareUrl,
  highlightsShareUrl,
  clipIndex,
  isRewindPlayer,
  onShareUrlChange,
  spotId,
  spotName,
  cameraId,
  cameraTitle,
  currentPlayer,
}) => {
  const [copied, setCopied] = useState(false);
  const [isChecked, setIsChecked] = useState(isRewindPlayer || currentPlayer === Player.HIGHLIGHTS);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
    setCopied(false);
  };

  const shareUrl = useMemo(() => {
    if (isRewindPlayer && isChecked) {
      return rewindShareUrl;
    } else if (currentPlayer === Player.HIGHLIGHTS && isChecked) {
      return highlightsShareUrl;
    } else {
      return liveCamShareUrl;
    }
  }, [
    isRewindPlayer,
    isChecked,
    currentPlayer,
    rewindShareUrl,
    highlightsShareUrl,
    liveCamShareUrl,
  ]);

  useEffect(() => {
    onShareUrlChange(shareUrl);
  }, [shareUrl, onShareUrlChange]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator?.clipboard?.writeText(shareUrl);
      setCopied(true);

      trackEvent('Button Clicked', {
        buttonText: 'Copy Link',
        pageName: 'Spot Page',
        uiElement: `Copy Link Button - ${isChecked ? 'Rewind' : 'Live'}`,
        spotId,
        spotName,
        cameraId,
        cameraName: cameraTitle,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [shareUrl, isChecked, spotId, spotName, cameraId, cameraTitle]);

  const checkboxCopy = useMemo(() => {
    if (currentPlayer === Player.HIGHLIGHTS && clipIndex !== undefined) {
      return `Start video at clip ${clipIndex + 1}`;
    }
    return `Start video at ${formattedTime}`;
  }, [currentPlayer, formattedTime, clipIndex]);

  return (
    <Box className={styles.shareLinkContainer}>
      <Box className={styles.shareLinkWrapper}>
        <Box className={styles.shareLinkInner}>
          <Box className={styles.shareLink}>
            <Typography
              data-testid="share-link"
              variant="footnote"
              className={`${styles.shareLink} ${copied ? styles.copied : ''}`}
            >
              {shareUrl}
            </Typography>
          </Box>
        </Box>
      </Box>
      {(isRewindPlayer || currentPlayer === Player.HIGHLIGHTS) && (
        <Box className={styles.checkboxContainer}>
          <FormControlLabel
            control={
              <Checkbox
                data-testid="play-at-timestamp-checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsChecked(!isChecked);
                    setCopied(false);
                  }
                }}
                color="secondary"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                icon={<CheckedCircleOutlineIcon fill="#E4E7ED" />}
                checkedIcon={<CheckedCircleHeavyIcon />}
                role="checkbox"
              />
            }
            label={<Typography variant="callout2">{checkboxCopy}</Typography>}
          />
        </Box>
      )}
      <Button
        className={styles.copyLinkButton}
        data-testid="copy-share-link-button"
        onClick={handleCopyLink}
        variant="primary"
        size="large"
      >
        {copied ? (
          <Box className={styles.buttonContent}>
            <TickIcon className={styles.tickIcon} />
            <span>Link copied!</span>
          </Box>
        ) : (
          'Copy link'
        )}
      </Button>
    </Box>
  );
};

export default ShareForm;
